import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Tag, Space, Modal, message, Select, Input, Typography, Descriptions, Popconfirm
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, BankOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const adminApi = axios.create({ baseURL: '/api', timeout: 15000 });
adminApi.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
adminApi.interceptors.response.use(r => r, error => {
  if (error.response?.status === 401) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    window.location.href = '/admin/login';
  }
  return Promise.reject(error);
});

const { Option } = Select;
const { TextArea } = Input;

const verificationColor = { pending: 'orange', verified: 'green', rejected: 'red' };
const verificationLabel = { pending: 'Menunggu', verified: 'Terverifikasi', rejected: 'Ditolak' };

const AdminMerchantBankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [detailVisible, setDetailVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { fetchAccounts(); }, [filterStatus]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/admin/bank-accounts', {
        params: { status: filterStatus }
      });
      setAccounts(res.data?.data || []);
    } catch (err) {
      message.error('Gagal memuat data rekening');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (id, action, notes = '') => {
    setActionLoading(true);
    try {
      await adminApi.put(`/admin/bank-accounts/${id}/verify`, { action, notes });
      message.success(action === 'approve' ? 'Rekening disetujui' : 'Rekening ditolak');
      setDetailVisible(false);
      setRejectVisible(false);
      setRejectNotes('');
      fetchAccounts();
    } catch (err) {
      message.error(err.response?.data?.error || 'Gagal memproses');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{r.merchant_name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.merchant_email}</div>
        </div>
      )
    },
    {
      title: 'Bank',
      key: 'bank',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{r.bank_name}</div>
          {r.branch_name && <div style={{ fontSize: 12, color: '#888' }}>{r.branch_name}</div>}
        </div>
      )
    },
    {
      title: 'Nomor Rekening',
      key: 'account',
      render: (_, r) => (
        <div>
          <div style={{ fontFamily: 'monospace' }}>{r.account_number}</div>
          <div style={{ fontSize: 12 }}>{r.account_name}</div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'verification_status',
      key: 'status',
      render: (s) => <Tag color={verificationColor[s]}>{verificationLabel[s]}</Tag>
    },
    {
      title: 'Tgl Daftar',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Aksi',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelected(r); setDetailVisible(true); }}>
            Detail
          </Button>
          {r.verification_status === 'pending' && (
            <>
              <Button
                size="small" type="primary" icon={<CheckCircleOutlined />}
                onClick={() => handleVerify(r.id, 'approve')}
                loading={actionLoading}
              >
                Setujui
              </Button>
              <Button
                size="small" danger icon={<CloseCircleOutlined />}
                onClick={() => { setSelected(r); setRejectVisible(true); }}
              >
                Tolak
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title={<Space><BankOutlined /><span>Verifikasi Rekening Bank Merchant</span></Space>}
        extra={
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 180 }}>
            <Option value="">Semua</Option>
            <Option value="pending">Menunggu</Option>
            <Option value="verified">Terverifikasi</Option>
            <Option value="rejected">Ditolak</Option>
          </Select>
        }
      >
        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: 'Tidak ada data' }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Detail Rekening Bank"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={selected?.verification_status === 'pending' ? (
          <Space>
            <Button onClick={() => setDetailVisible(false)}>Tutup</Button>
            <Button danger icon={<CloseCircleOutlined />} onClick={() => { setDetailVisible(false); setRejectVisible(true); }}>
              Tolak
            </Button>
            <Button type="primary" icon={<CheckCircleOutlined />} loading={actionLoading}
              onClick={() => handleVerify(selected.id, 'approve')}>
              Setujui
            </Button>
          </Space>
        ) : (
          <Button onClick={() => setDetailVisible(false)}>Tutup</Button>
        )}
      >
        {selected && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Merchant">{selected.merchant_name}</Descriptions.Item>
            <Descriptions.Item label="Email">{selected.merchant_email}</Descriptions.Item>
            <Descriptions.Item label="Bank">{selected.bank_name}</Descriptions.Item>
            <Descriptions.Item label="Cabang">{selected.branch_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Nomor Rekening">
              <span style={{ fontFamily: 'monospace', fontSize: 15 }}>{selected.account_number}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Nama Pemilik">{selected.account_name}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={verificationColor[selected.verification_status]}>
                {verificationLabel[selected.verification_status]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Rekening Utama">{selected.is_primary ? 'Ya' : 'Tidak'}</Descriptions.Item>
            <Descriptions.Item label="Tgl Daftar">
              {dayjs(selected.created_at).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Tolak Rekening Bank"
        open={rejectVisible}
        onCancel={() => { setRejectVisible(false); setRejectNotes(''); }}
        footer={
          <Space>
            <Button onClick={() => { setRejectVisible(false); setRejectNotes(''); }}>Batal</Button>
            <Button danger loading={actionLoading}
              onClick={() => handleVerify(selected?.id, 'reject', rejectNotes)}>
              Konfirmasi Tolak
            </Button>
          </Space>
        }
      >
        <p>Alasan penolakan (opsional):</p>
        <TextArea rows={3} value={rejectNotes} onChange={e => setRejectNotes(e.target.value)}
          placeholder="Contoh: Nama rekening tidak sesuai dengan nama merchant" />
      </Modal>
    </div>
  );
};

export default AdminMerchantBankAccounts;
